// FILE: src/components/TelemetryDashboard.js
//
// Admin → Telemetry: operational visibility into platform growth, usage,
// engagement, conversion, and technical health.
//
// COMPLEMENTS the existing Admin → Dashboard (question banks) and Question
// Performance (per-question analytics). This view never duplicates those —
// it reads telemetry_events aggregates through the admin-gated RPCs in
// migration 026 (and 030 for the error drill-down).
//
// Reuses the AdminDashboard visual language (dark header, light body, stat
// cards) and recharts for trends/bars. Responsive via useMediaQuery.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ResponsiveContainer,
  LineChart, Line,
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import useMediaQuery from '../../../hooks/useMediaQuery';
import {
  createLoadRecorder, mergeServerTimings, diagnoseLoad, SLOW_DASHBOARD_LOAD_MS,
} from '../../../services/dashboardLoadTiming';
import {
  fetchActiveUsers,
  fetchDailyActive,
  fetchEventCounts,
  fetchEngagement,
  fetchTechnicalHealth,
  fetchMetadataBreakdown,
  fetchRegisteredUsersCount,
  fetchErrorDetails,
  fetchSlowQueryDetails,
  fetchDashboardServerTimings,
  trackEvent,
  deriveGrowthKpis,
  toDailyActiveSeries,
  deriveConversionFunnel,
} from '../../../services/telemetryService';

// ── Design tokens (mirror AdminDashboard) ──────────────────────────────────────

const BRAND = {
  headerBg: '#1a1a2e',
  pageBg:   '#f5f5f5',
  cardBg:   '#fff',
  border:   '#e0e0e0',
  text:     '#222',
  textMuted:'#666',
  textLight:'#999',
  accent:   '#1565c0',
  danger:   '#c62828',
  fontFamily: "'Segoe UI', system-ui, sans-serif",
};

const CHART_COLORS = ['#1565c0', '#2e7d32', '#e07b00', '#6a1b9a', '#00838f', '#c62828', '#555'];

const S = {
  wrapper: { fontFamily: BRAND.fontFamily, minHeight: '100vh', background: BRAND.pageBg, display: 'flex', flexDirection: 'column' },
  header:  { background: BRAND.headerBg, color: '#fff', padding: '14px 28px', display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0, flexWrap: 'wrap' },
  backButton: { padding: '6px 14px', background: 'transparent', border: '1px solid #555', borderRadius: '4px', color: '#ccc', cursor: 'pointer', fontSize: '0.85rem' },
  headerTitle: { fontSize: '1.1rem', fontWeight: 'bold', flex: 1, letterSpacing: '0.5px' },
  headerMeta: { fontSize: '0.75rem', color: '#aaa', display: 'flex', alignItems: 'center', gap: '10px' },
  refreshButton: { padding: '5px 12px', background: 'transparent', border: '1px solid #555', borderRadius: '4px', color: '#ccc', cursor: 'pointer', fontSize: '0.78rem' },
  rangeBtn: (active) => ({ padding: '5px 12px', background: active ? BRAND.accent : 'transparent', border: `1px solid ${active ? BRAND.accent : '#555'}`, borderRadius: '4px', color: active ? '#fff' : '#ccc', cursor: 'pointer', fontSize: '0.78rem' }),
  body: { flex: 1, padding: '28px 32px', maxWidth: '1200px', margin: '0 auto', width: '100%', boxSizing: 'border-box' },
  sectionTitle: { fontSize: '0.95rem', fontWeight: 600, color: BRAND.textMuted, textTransform: 'uppercase', letterSpacing: '1px', margin: '28px 0 14px' },
  tileGrid: (cols) => ({ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '16px' }),
  tile: { background: BRAND.cardBg, border: `1px solid ${BRAND.border}`, borderRadius: '10px', padding: '18px 20px' },
  // Same look as `tile`, but tagged interactive so users discover the click affordance.
  tileClickable: {
    background: BRAND.cardBg, border: `1px solid ${BRAND.border}`, borderRadius: '10px', padding: '18px 20px',
    cursor: 'pointer', textAlign: 'left', width: '100%', font: 'inherit', color: 'inherit',
    transition: 'border-color 0.12s ease, box-shadow 0.12s ease',
  },
  tileLabel: { fontSize: '0.75rem', color: BRAND.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' },
  tileValue: { fontSize: '1.9rem', fontWeight: 700, color: BRAND.text, marginTop: '6px', lineHeight: 1 },
  tileHint: { fontSize: '0.72rem', color: BRAND.textLight, marginTop: '6px' },
  tileHintAction: { fontSize: '0.72rem', color: BRAND.accent, marginTop: '6px', fontWeight: 600 },
  card: { background: BRAND.cardBg, border: `1px solid ${BRAND.border}`, borderRadius: '10px', padding: '20px', marginBottom: '20px' },
  cardTitle: { fontSize: '0.95rem', fontWeight: 600, color: BRAND.text, marginBottom: '14px' },
  twoCol: (stack) => ({ display: 'grid', gridTemplateColumns: stack ? '1fr' : '1fr 1fr', gap: '20px' }),
  funnelRow: { display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${BRAND.border}`, fontSize: '0.9rem', color: BRAND.text },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' },
  th: { textAlign: 'left', padding: '6px 8px', color: BRAND.textMuted, borderBottom: `2px solid ${BRAND.border}`, fontWeight: 600 },
  td: { padding: '6px 8px', borderBottom: `1px solid ${BRAND.border}`, color: BRAND.text },
  msg: { textAlign: 'center', color: BRAND.textMuted, padding: '60px 0', fontSize: '0.95rem' },

  // ── Modal ──────────────────────────────────────────────────────────────────
  modalScrim: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px',
  },
  modal: {
    background: BRAND.cardBg, borderRadius: '12px', width: '100%', maxWidth: '880px',
    maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
    boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
  },
  modalHeader: {
    padding: '16px 22px', background: BRAND.headerBg, color: '#fff',
    display: 'flex', alignItems: 'center', gap: '14px', flexShrink: 0,
  },
  modalTitle: { fontSize: '1rem', fontWeight: 'bold', flex: 1, letterSpacing: '0.5px' },
  modalClose: {
    padding: '5px 12px', background: 'transparent', border: '1px solid #555',
    borderRadius: '4px', color: '#ccc', cursor: 'pointer', fontSize: '0.85rem',
  },
  modalAction: {
    padding: '5px 12px', background: 'transparent', border: '1px solid #555',
    borderRadius: '4px', color: '#ccc', cursor: 'pointer', fontSize: '0.85rem',
  },
  modalActionPrimary: {
    padding: '5px 12px', background: '#1565c0', border: '1px solid #1565c0',
    borderRadius: '4px', color: '#fff', cursor: 'pointer', fontSize: '0.85rem',
    fontWeight: 600,
  },
  insightBox: {
    background: '#f6f8fa', border: '1px solid #d0d7de', borderRadius: '8px',
    padding: '14px 16px', marginBottom: '14px', fontSize: '0.88rem',
    color: '#222', lineHeight: 1.55, whiteSpace: 'pre-wrap',
  },
  insightLabel: {
    fontSize: '0.72rem', color: '#555', textTransform: 'uppercase',
    letterSpacing: '0.6px', fontWeight: 600, marginBottom: '6px',
  },
  modalBody: { padding: '18px 22px', overflowY: 'auto', flex: 1 },
  modalFooter: {
    padding: '10px 22px', borderTop: `1px solid ${BRAND.border}`,
    fontSize: '0.78rem', color: BRAND.textMuted, flexShrink: 0,
  },
  groupRow: {
    border: `1px solid ${BRAND.border}`, borderRadius: '8px', marginBottom: '10px',
    background: '#fafafa',
  },
  groupHead: {
    padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '12px',
    cursor: 'pointer', width: '100%', background: 'transparent', border: 'none',
    textAlign: 'left', font: 'inherit', color: 'inherit',
  },
  groupCount: {
    display: 'inline-block', minWidth: '34px', textAlign: 'center',
    padding: '2px 8px', borderRadius: '999px', background: BRAND.danger,
    color: '#fff', fontWeight: 700, fontSize: '0.8rem',
  },
  groupMsg: { flex: 1, fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: '0.85rem', color: BRAND.text, wordBreak: 'break-word' },
  groupMeta: { fontSize: '0.72rem', color: BRAND.textMuted, whiteSpace: 'nowrap' },
  groupDetail: {
    padding: '0 14px 12px',
    borderTop: `1px solid ${BRAND.border}`,
    background: '#fff',
  },
};

const RANGES = [
  { key: '7d',  days: 7,  label: '7 days' },
  { key: '30d', days: 30, label: '30 days' },
  { key: '90d', days: 90, label: '90 days' },
];

function rangeWindow(days) {
  const to = new Date();
  const from = new Date();
  from.setUTCDate(from.getUTCDate() - (days - 1));
  return { from, to };
}

function fmtDuration(ms) {
  if (ms === null || ms === undefined || Number.isNaN(Number(ms))) return '—';
  const totalSec = Math.round(Number(ms) / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function num(v) {
  return (v === null || v === undefined) ? '—' : v;
}

function fmtTime(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch (_) { return String(iso); }
}

function shortId(id) {
  if (!id) return '—';
  const s = String(id);
  return s.length > 8 ? `${s.slice(0, 8)}…` : s;
}

const DIAGNOSIS_LABEL = {
  database: 'mostly database time',
  network_or_api: 'mostly network / API time (database was fast)',
  render: 'mostly browser rendering',
  data: 'waiting on data',
};

function LoadTiming({ timing, open, onToggle }) {
  const ms = (v) => (v == null ? '—' : `${v} ms`);
  const slow = timing.totalMs != null && timing.totalMs >= SLOW_DASHBOARD_LOAD_MS;
  return (
    <div data-testid="telemetry-load-timing" style={{ fontSize: '0.78rem', color: slow ? '#b45309' : BRAND.textLight, margin: '0 0 12px' }}>
      Loaded in {ms(timing.totalMs)} (data {ms(timing.dataMs)} · render {ms(timing.renderMs)} · {timing.requestCount} requests)
      {timing.serverCaptured && timing.diagnosis && <> — {DIAGNOSIS_LABEL[timing.diagnosis]}</>}
      {' '}
      <button type="button" onClick={onToggle} data-testid="telemetry-load-timing-toggle"
        style={{ background: 'none', border: 'none', color: '#1565c0', cursor: 'pointer', fontSize: '0.78rem', padding: 0 }}>
        {open ? 'hide timing' : 'timing details'}
      </button>
      {open && (
        <table data-testid="telemetry-load-timing-table" style={{ marginTop: 6, borderCollapse: 'collapse', fontSize: '0.75rem' }}>
          <thead>
            <tr>
              {['Request', 'Starts at', 'Browser wall', 'DB exec (now)', 'Network + API'].map((h) => (
                <th key={h} style={{ textAlign: 'left', padding: '2px 10px 2px 0', fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {timing.requests.map((r) => (
              <tr key={r.label}>
                <td style={{ padding: '2px 10px 2px 0', fontFamily: 'monospace' }}>{r.label}{r.ok ? '' : ' (failed)'}</td>
                <td style={{ padding: '2px 10px 2px 0' }}>+{r.startMs} ms</td>
                <td style={{ padding: '2px 10px 2px 0' }}>{ms(r.ms)}</td>
                <td style={{ padding: '2px 10px 2px 0' }}>{timing.serverCaptured ? ms(r.serverMs) : '…'}</td>
                <td style={{ padding: '2px 10px 2px 0' }}>{timing.serverCaptured ? ms(r.overheadMs) : '…'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function TelemetryDashboard({ onBack }) {
  const isNarrow = useMediaQuery('(max-width: 760px)');

  const [rangeKey, setRangeKey] = useState('30d');
  const [loading, setLoading]   = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [data, setData] = useState(null);

  // Tech-health drill-down modal state. Generic over 'error' and 'slow_query'
  // — kept local to this component, doesn't justify a context.
  const [drilldownKind, setDrilldownKind] = useState(null); // 'error' | 'slow_query' | null
  const [drilldownData, setDrilldownData] = useState(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);

  // Load-timing instrumentation: one recorder per load; the render mark is
  // taken on the first animation frame after React commits the new data.
  const [timing, setTiming] = useState(null);
  const [timingOpen, setTimingOpen] = useState(false);
  const loadRef = useRef({ id: 0, recorder: null, window: null, pendingRender: false });

  const load = useCallback(async () => {
    setLoading(true);
    const days = (RANGES.find((r) => r.key === rangeKey) || RANGES[1]).days;
    const { from, to } = rangeWindow(days);
    const loadId = loadRef.current.id + 1;
    const rec = createLoadRecorder();
    loadRef.current = { id: loadId, recorder: rec, window: { from, to, rangeKey }, pendingRender: false };
    const t = rec.track;

    const [
      activeUsers, registered, daily, engagement, tech, growthCounts, modeUsage, certUsage,
    ] = await Promise.all([
      t('telemetry_active_users', fetchActiveUsers(to)),
      t('telemetry_registered_accounts', fetchRegisteredUsersCount()),
      t('telemetry_daily_active', fetchDailyActive(from, to)),
      t('telemetry_engagement', fetchEngagement(from, to)),
      t('telemetry_technical_health', fetchTechnicalHealth(from, to)),
      t('telemetry_event_counts', fetchEventCounts(from, to, 'growth')),
      t('telemetry_metadata_breakdown:mode', fetchMetadataBreakdown(from, to, 'mode_selected', 'mode')),
      t('telemetry_metadata_breakdown:cert', fetchMetadataBreakdown(from, to, 'cert_selected', 'cert')),
    ]);
    if (loadRef.current.id !== loadId) return; // a newer load superseded this one
    rec.markDataSettled();
    loadRef.current.pendingRender = true;

    setData({
      kpis: deriveGrowthKpis(activeUsers, registered),
      series: toDailyActiveSeries(daily, from, to),
      engagement: engagement || {},
      tech: tech || {},
      funnel: deriveConversionFunnel(growthCounts),
      modeUsage: (modeUsage || []).map((r) => ({ label: r.label, n: Number(r.n) || 0 })),
      certUsage: (certUsage || []).map((r) => ({ label: r.label, n: Number(r.n) || 0 })),
    });
    setLastRefreshed(new Date());
    setLoading(false);
  }, [rangeKey]);

  useEffect(() => { load(); }, [load]);

  // Server-side execution timings for the current window, merged into the
  // client report. Captured after the fact, so they describe the database now.
  const captureServerTimings = useCallback(async (report) => {
    const w = loadRef.current.window;
    if (!w || !report) return report;
    const server = await fetchDashboardServerTimings(w.from, w.to);
    const merged = mergeServerTimings(report, server);
    return { ...merged, diagnosis: diagnoseLoad(merged), serverCaptured: !!server };
  }, []);

  useEffect(() => {
    const cur = loadRef.current;
    if (!data || !cur.pendingRender || !cur.recorder) return undefined;
    cur.pendingRender = false;
    const loadId = cur.id;
    const raf = typeof window !== 'undefined' && window.requestAnimationFrame
      ? window.requestAnimationFrame : (fn) => setTimeout(fn, 0);
    raf(async () => {
      if (loadRef.current.id !== loadId) return;
      cur.recorder.markRendered();
      const summary = cur.recorder.summary();
      const report = { ...summary, diagnosis: diagnoseLoad(summary), rangeKey: cur.window?.rangeKey };
      setTiming(report);
      // eslint-disable-next-line no-console
      console.info('[telemetry-timing]', report);
      if (summary.totalMs != null && summary.totalMs >= SLOW_DASHBOARD_LOAD_MS) {
        const full = await captureServerTimings(report);
        if (loadRef.current.id !== loadId) return;
        setTiming(full);
        // eslint-disable-next-line no-console
        console.warn('[telemetry-timing] slow load', full);
        trackEvent('admin_dashboard_timing', 'technical', {
          target: 'telemetry',
          view: `telemetry:${cur.window?.rangeKey || ''}`,
          duration_ms: full.totalMs,
          data_ms: full.dataMs,
          render_ms: full.renderMs,
          request_count: full.requestCount,
          slowest_target: full.slowest?.label,
          slowest_ms: full.slowest?.ms,
          server_ms: full.maxServerMs,
          diagnosis: full.diagnosis,
          status: 'slow',
        });
      }
    });
    return undefined;
  }, [data, captureServerTimings]);

  const onTimingDetails = useCallback(async () => {
    setTimingOpen((o) => !o);
    if (timing && !timing.serverCaptured) {
      const full = await captureServerTimings(timing);
      setTiming(full);
    }
  }, [timing, captureServerTimings]);

  const openDrilldown = useCallback(async (kind) => {
    const days = (RANGES.find((r) => r.key === rangeKey) || RANGES[1]).days;
    const { from, to } = rangeWindow(days);
    setDrilldownKind(kind);
    setDrilldownLoading(true);
    setDrilldownData(null);
    const fetcher = kind === 'slow_query' ? fetchSlowQueryDetails : fetchErrorDetails;
    const details = await fetcher(from, to);
    setDrilldownData(details);
    setDrilldownLoading(false);
  }, [rangeKey]);

  const closeDrilldown = useCallback(() => {
    setDrilldownKind(null);
  }, []);

  const k = data?.kpis;
  // Stickiness (DAU/MAU) — a standard engagement ratio; returning-user cohorts
  // are a later-phase addition (the pure helpers exist in telemetryService).
  const stickiness = k && k.mau ? Math.round((k.dau / k.mau) * 100) : null;

  // The view has data if any meaningful signal is non-zero.
  const hasData = !!data && (
    (k && (k.dau || k.wau || k.mau)) ||
    (data.series && data.series.some((d) => d.events > 0)) ||
    (data.engagement && data.engagement.app_sessions)
  );

  const errorCount = data?.tech?.errors || 0;
  const slowCount  = data?.tech?.slow_events || 0;
  const errorsClickable = errorCount > 0;
  const slowsClickable  = slowCount > 0;

  return (
    <div style={S.wrapper} data-testid="telemetry-dashboard">
      <div style={S.header}>
        <button style={S.backButton} onClick={onBack} data-testid="telemetry-back-button">← Back</button>
        <span style={S.headerTitle}>📡 Telemetry</span>
        <div style={S.headerMeta}>
          {RANGES.map((r) => (
            <button key={r.key} style={S.rangeBtn(r.key === rangeKey)} onClick={() => setRangeKey(r.key)}>
              {r.label}
            </button>
          ))}
          <span>{lastRefreshed ? `Updated ${lastRefreshed.toLocaleTimeString()}` : ''}</span>
          <button style={S.refreshButton} onClick={load}>↻ Refresh</button>
        </div>
      </div>

      <div style={S.body}>
        {loading && <div style={S.msg}>Loading telemetry…</div>}

        {!loading && timing && (
          <LoadTiming timing={timing} open={timingOpen} onToggle={onTimingDetails} />
        )}

        {!loading && !hasData && (
          <div style={S.msg} data-testid="telemetry-empty">
            No telemetry recorded yet for this window. Activity will appear here as learners use the app.
          </div>
        )}

        {!loading && hasData && (
          <>
            {/* ── Product Growth ──────────────────────────────────────────── */}
            <div style={S.sectionTitle}>Product Growth</div>
            <div style={S.tileGrid(isNarrow ? 2 : 4)}>
              <div style={S.tile}><div style={S.tileLabel}>DAU</div><div style={S.tileValue}>{num(k.dau)}</div><div style={S.tileHint}>active today</div></div>
              <div style={S.tile}><div style={S.tileLabel}>WAU</div><div style={S.tileValue}>{num(k.wau)}</div><div style={S.tileHint}>last 7 days</div></div>
              <div style={S.tile}><div style={S.tileLabel}>MAU</div><div style={S.tileValue}>{num(k.mau)}</div><div style={S.tileHint}>last 30 days</div></div>
              <div style={S.tile} data-testid="telemetry-registered-tile"><div style={S.tileLabel}>Registered</div><div style={S.tileValue}>{num(k.registered)}</div><div style={S.tileHint}>real accounts</div></div>
              <div style={S.tile}><div style={S.tileLabel}>Stickiness</div><div style={S.tileValue}>{stickiness === null ? '—' : `${stickiness}%`}</div><div style={S.tileHint}>DAU / MAU</div></div>
              <div style={S.tile}><div style={S.tileLabel}>App sessions</div><div style={S.tileValue}>{num(data.engagement.app_sessions)}</div><div style={S.tileHint}>visits in range</div></div>
              <div style={S.tile}><div style={S.tileLabel}>Avg duration</div><div style={S.tileValue}>{fmtDuration(data.engagement.avg_duration_ms)}</div><div style={S.tileHint}>per visit</div></div>
              <div style={S.tile}><div style={S.tileLabel}>Avg pages</div><div style={S.tileValue}>{num(data.engagement.avg_pages)}</div><div style={S.tileHint}>per visit</div></div>
            </div>

            {/* ── Trend ───────────────────────────────────────────────────── */}
            <div style={S.sectionTitle}>Activity Trend</div>
            <div style={S.card}>
              <div style={S.cardTitle}>Daily active users &amp; events</div>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={data.series} margin={{ top: 6, right: 16, bottom: 6, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="day" tick={{ fontSize: 11 }} minTickGap={24} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="active_users" name="Active users" stroke={CHART_COLORS[0]} dot={false} strokeWidth={2} />
                  <Line type="monotone" dataKey="events" name="Events" stroke={CHART_COLORS[2]} dot={false} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* ── Usage / Engagement ──────────────────────────────────────── */}
            <div style={S.sectionTitle}>Usage</div>
            <div style={S.twoCol(isNarrow)}>
              <div style={S.card}>
                <div style={S.cardTitle}>Exam mode usage</div>
                {data.modeUsage.length === 0 ? (
                  <div style={{ color: BRAND.textLight, fontSize: '0.85rem' }}>No mode selections in range.</div>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={data.modeUsage} margin={{ top: 6, right: 16, bottom: 6, left: -10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="n" name="Selections" fill={CHART_COLORS[1]} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
              <div style={S.card}>
                <div style={S.cardTitle}>Certification selection</div>
                {data.certUsage.length === 0 ? (
                  <div style={{ color: BRAND.textLight, fontSize: '0.85rem' }}>No cert selections in range.</div>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={data.certUsage} margin={{ top: 6, right: 16, bottom: 6, left: -10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="n" name="Selections" fill={CHART_COLORS[3]} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* ── Conversion ──────────────────────────────────────────────── */}
            <div style={S.sectionTitle}>Conversion</div>
            <div style={S.card}>
              <div style={S.funnelRow}><span>Registrations started</span><strong>{data.funnel.register_started}</strong></div>
              <div style={S.funnelRow}><span>Registrations completed</span><strong>{data.funnel.register_completed}</strong></div>
              <div style={S.funnelRow}><span>Anonymous → registered</span><strong>{data.funnel.anonymous_to_registered}</strong></div>
              <div style={S.funnelRow}><span>Sign-ins</span><strong>{data.funnel.signin}</strong></div>
              <div style={{ ...S.funnelRow, borderBottom: 'none' }}><span>Failed sign-ins</span><strong>{data.funnel.signin_failed}</strong></div>
            </div>

            {/* ── Technical Health ────────────────────────────────────────── */}
            <div style={S.sectionTitle}>Technical Health</div>
            <div style={S.tileGrid(isNarrow ? 2 : 3)}>
              {errorsClickable ? (
                <button
                  type="button"
                  style={S.tileClickable}
                  onClick={() => openDrilldown('error')}
                  data-testid="telemetry-errors-tile"
                  aria-label={`View details for ${errorCount} errors`}
                >
                  <div style={S.tileLabel}>Errors</div>
                  <div style={{ ...S.tileValue, color: BRAND.danger }}>{num(errorCount)}</div>
                  <div style={S.tileHintAction}>Click to view details →</div>
                </button>
              ) : (
                <div style={S.tile} data-testid="telemetry-errors-tile">
                  <div style={S.tileLabel}>Errors</div>
                  <div style={S.tileValue}>{num(errorCount)}</div>
                </div>
              )}
              <div style={S.tile}><div style={S.tileLabel}>Failed loads</div><div style={S.tileValue}>{num(data.tech.failed_loads)}</div></div>
              {slowsClickable ? (
                <button
                  type="button"
                  style={S.tileClickable}
                  onClick={() => openDrilldown('slow_query')}
                  data-testid="telemetry-slow-tile"
                  aria-label={`View details for ${slowCount} slow queries`}
                >
                  <div style={S.tileLabel}>Slow queries</div>
                  <div style={{ ...S.tileValue, color: BRAND.danger }}>{num(slowCount)}</div>
                  <div style={S.tileHintAction}>Click to view details →</div>
                </button>
              ) : (
                <div style={S.tile} data-testid="telemetry-slow-tile">
                  <div style={S.tileLabel}>Slow queries</div>
                  <div style={S.tileValue}>{num(slowCount)}</div>
                </div>
              )}
            </div>
            <div style={{ ...S.twoCol(isNarrow), marginTop: '20px' }}>
              <div style={S.card}>
                <div style={S.cardTitle}>Browser mix</div>
                <MixTable mix={data.tech.by_browser} />
              </div>
              <div style={S.card}>
                <div style={S.cardTitle}>Device mix</div>
                <MixTable mix={data.tech.by_device} />
              </div>
            </div>
          </>
        )}
      </div>

      {drilldownKind && (
        <DrilldownModal
          kind={drilldownKind}
          rangeLabel={(RANGES.find((r) => r.key === rangeKey) || RANGES[1]).label}
          loading={drilldownLoading}
          details={drilldownData}
          onClose={closeDrilldown}
        />
      )}
    </div>
  );
}

function MixTable({ mix }) {
  const entries = mix && typeof mix === 'object' ? Object.entries(mix) : [];
  if (entries.length === 0) {
    return <div style={{ color: BRAND.textLight, fontSize: '0.85rem' }}>No session data in range.</div>;
  }
  entries.sort((a, b) => b[1] - a[1]);
  return (
    <table style={S.table}>
      <thead>
        <tr><th style={S.th}>Name</th><th style={S.th}>Sessions</th></tr>
      </thead>
      <tbody>
        {entries.map(([name, n]) => (
          <tr key={name}><td style={S.td}>{name}</td><td style={S.td}>{n}</td></tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Tech-health drill-down modal ────────────────────────────────────────────
// Generic over `kind`: 'error' groups by message_short, 'slow_query' groups
// by target with duration stats. The shape from both RPCs is shared
// ({total, groups[], rows[]}); only the grouping key + column set differ.

const DRILLDOWN_CONFIG = {
  error: {
    title:        'Error details',
    eventName:    'error',
    groupKey:     'message_short',
    rowKey:       'message_short',
    emptyMessage: 'No errors recorded in this window.',
    loadingMessage: 'Loading errors…',
    countNoun:    'error',
    groupNoun:    'message',
    sourceFootnote: "Only the truncated message_short is stored — no stack traces, no PII.",
    columns: [
      { key: 'when',        label: 'When (UTC-local)' },
      { key: 'view',        label: 'View' },
      { key: 'code',        label: 'Code' },
      { key: 'actor',       label: 'Actor' },
      { key: 'app_version', label: 'App version' },
    ],
  },
  slow_query: {
    title:        'Slow-query details',
    eventName:    'slow_query',
    groupKey:     'target',
    rowKey:       'target',
    emptyMessage: 'No slow queries recorded in this window.',
    loadingMessage: 'Loading slow queries…',
    countNoun:    'slow query',
    groupNoun:    'target',
    sourceFootnote: "Threshold = 2000ms; emitted from timeRead() in telemetryService.",
    columns: [
      { key: 'when',        label: 'When (UTC-local)' },
      { key: 'duration',    label: 'Duration' },
      { key: 'view',        label: 'View' },
      { key: 'actor',       label: 'Actor' },
      { key: 'app_version', label: 'App version' },
    ],
  },
};

function fmtMs(v) {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n >= 1000 ? `${(n / 1000).toFixed(2)}s` : `${Math.round(n)}ms`;
}

// ── Export helpers ────────────────────────────────────────────────────────
// Pure, client-side. The drill-down RPCs already return a tidy
// {total, groups, rows} payload; we just pick the columns the admin sees in
// the modal and serialise them. No PII leaves the modal that wasn't already
// rendered in it.

function csvEscape(v) {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function rowsToCsv(rows, columns) {
  const head = columns.map((c) => csvEscape(c.label)).join(',');
  const body = rows.map((r) => columns.map((c) => csvEscape(r[c.key])).join(',')).join('\n');
  return `${head}\n${body}\n`;
}

function triggerDownload(filename, mime, content) {
  try {
    const blob = new Blob([content], { type: mime });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (err) {
    console.warn('[telemetry] download failed:', err?.message || err);
  }
}

function buildExportFilename(kind, ext) {
  const stamp = new Date().toISOString().slice(0, 10);
  return `telemetry-${kind === 'slow_query' ? 'slow-queries' : 'errors'}-${stamp}.${ext}`;
}

// ── Analyze (Netlify Function → Claude) ───────────────────────────────────
// The function endpoint is /.netlify/functions/telemetry-analyze. It verifies
// the caller's admin status via the existing _is_caller_admin() RPC and pipes
// the drill-down payload + a structured prompt to the Anthropic API.
// Returns { insights: string } on success.
async function fetchInsights(kind, details) {
  const { supabase } = await import('../../../services/supabaseClient');
  const { data: sess } = await supabase.auth.getSession();
  const token = sess?.session?.access_token;
  if (!token) {
    throw new Error('Sign in as an admin first.');
  }
  const res = await fetch('/.netlify/functions/telemetry-analyze', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ kind, details }),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(payload?.error || `Analyze failed (${res.status}).`);
  }
  return payload.insights || '(no insights returned)';
}

function DrilldownModal({ kind, rangeLabel, loading, details, onClose }) {
  // Close on Escape — small ergonomic touch, no dependency on a modal library.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const cfg = DRILLDOWN_CONFIG[kind] || DRILLDOWN_CONFIG.error;
  const [expanded, setExpanded] = useState(null);
  const [insights, setInsights]           = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);
  const [insightError, setInsightError]   = useState(null);

  const groups = details?.groups || [];
  const total  = details?.total  || 0;
  // Memoised so the rowsByGroup useMemo below has a stable dependency
  // (otherwise `details?.rows || []` creates a fresh [] every render).
  const rows = React.useMemo(() => details?.rows || [], [details]);

  // Pre-bucket per-occurrence rows by the active group key so expanding is O(1).
  const rowsByGroup = React.useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      const key = r[cfg.rowKey] || '(none)';
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(r);
    }
    return m;
  }, [rows, cfg.rowKey]);

  const handleExportCsv = () => {
    const rows = details?.rows || [];
    // Build per-occurrence columns matching the table view, plus the group key
    // and (for slow queries) duration_ms so the CSV is self-describing.
    const cols = [
      { key: 'created_at',    label: 'When (UTC)' },
      { key: cfg.rowKey,      label: cfg.groupKey === 'message_short' ? 'Message' : 'Target' },
      { key: 'duration_ms',   label: 'Duration (ms)' },
      { key: 'view',          label: 'View' },
      { key: 'code',          label: 'Code' },
      { key: 'user_id',       label: 'User ID' },
      { key: 'anon_id',       label: 'Anon ID' },
      { key: 'app_session_id',label: 'App session ID' },
      { key: 'app_version',   label: 'App version' },
    ];
    triggerDownload(buildExportFilename(kind, 'csv'), 'text/csv;charset=utf-8', rowsToCsv(rows, cols));
  };

  const handleExportJson = () => {
    // Whole payload — groups + rows + summary. Useful for piping into an LLM
    // or notebook where the roll-up structure matters as much as the raw rows.
    const payload = {
      kind,
      generated_at: new Date().toISOString(),
      from: details?.from || null,
      to:   details?.to   || null,
      total: details?.total || 0,
      groups: details?.groups || [],
      rows:   details?.rows   || [],
    };
    triggerDownload(buildExportFilename(kind, 'json'), 'application/json', JSON.stringify(payload, null, 2));
  };

  const handleAnalyze = async () => {
    setInsightLoading(true);
    setInsightError(null);
    setInsights(null);
    try {
      const text = await fetchInsights(kind, details);
      setInsights(text);
    } catch (err) {
      setInsightError(err?.message || String(err));
    } finally {
      setInsightLoading(false);
    }
  };

  const renderCell = (r, col) => {
    switch (col.key) {
      case 'when':        return fmtTime(r.created_at);
      case 'duration':    return fmtMs(r.duration_ms);
      case 'view':        return r.view || '—';
      case 'code':        return r.code || '—';
      case 'app_version': return r.app_version || '—';
      case 'actor':       return r.user_id
        ? <>user <code>{shortId(r.user_id)}</code></>
        : <>anon <code>{shortId(r.anon_id)}</code></>;
      default: return '—';
    }
  };

  return (
    <div
      style={S.modalScrim}
      onClick={onClose}
      role="presentation"
      data-testid="telemetry-drilldown-modal"
      data-kind={kind}
    >
      <div
        style={S.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="telemetry-drilldown-modal-title"
      >
        <div style={S.modalHeader}>
          <span id="telemetry-drilldown-modal-title" style={S.modalTitle}>
            {cfg.title} · last {rangeLabel}
          </span>
          <button
            style={S.modalAction}
            onClick={handleExportCsv}
            disabled={!details || !(details.rows && details.rows.length)}
            data-testid="telemetry-drilldown-export-csv"
            title="Download per-occurrence rows as CSV"
          >
            ⬇ CSV
          </button>
          <button
            style={S.modalAction}
            onClick={handleExportJson}
            disabled={!details || !details.total}
            data-testid="telemetry-drilldown-export-json"
            title="Download full {groups, rows} payload as JSON"
          >
            ⬇ JSON
          </button>
          <button
            style={S.modalActionPrimary}
            onClick={handleAnalyze}
            disabled={insightLoading || !details || !details.total}
            data-testid="telemetry-drilldown-analyze"
            title="Ask Claude to diagnose these events"
          >
            {insightLoading ? 'Analyzing…' : '✨ Analyze with Claude'}
          </button>
          <button
            style={S.modalClose}
            onClick={onClose}
            data-testid="telemetry-drilldown-modal-close"
          >
            Close
          </button>
        </div>

        <div style={S.modalBody}>
          {(insightLoading || insights || insightError) && (
            <div style={S.insightBox} data-testid="telemetry-drilldown-insights">
              <div style={S.insightLabel}>✨ Claude's analysis</div>
              {insightLoading && <div style={{ color: BRAND.textMuted }}>Asking Claude — usually 5–15s…</div>}
              {insightError   && <div style={{ color: BRAND.danger }}>{insightError}</div>}
              {insights       && <div>{insights}</div>}
            </div>
          )}
          {loading && <div style={S.msg}>{cfg.loadingMessage}</div>}

          {!loading && details && details._error && (
            <div style={{ ...S.msg, color: BRAND.danger }}>
              Failed to load details: {details._error}
            </div>
          )}

          {!loading && !details?._error && groups.length === 0 && (
            <div style={S.msg} data-testid="telemetry-drilldown-modal-empty">
              {cfg.emptyMessage}
            </div>
          )}

          {!loading && !details?._error && groups.length > 0 && (
            <>
              <div style={{ fontSize: '0.82rem', color: BRAND.textMuted, marginBottom: '12px' }}>
                <strong>{total}</strong> {cfg.countNoun}{total === 1 ? '' : 's'} across{' '}
                <strong>{groups.length}</strong> distinct {cfg.groupNoun}{groups.length === 1 ? '' : 's'}.
                Showing up to {rows.length} most-recent occurrences.
              </div>

              {groups.map((g) => {
                const groupKey = g[cfg.groupKey];
                const isOpen = expanded === groupKey;
                const occurrences = rowsByGroup.get(groupKey) || [];
                return (
                  <div key={groupKey} style={S.groupRow}>
                    <button
                      type="button"
                      style={S.groupHead}
                      onClick={() => setExpanded(isOpen ? null : groupKey)}
                      aria-expanded={isOpen}
                      data-testid="telemetry-drilldown-group"
                    >
                      <span style={S.groupCount}>{g.n}</span>
                      <span style={S.groupMsg}>{groupKey}</span>
                      <span style={S.groupMeta}>
                        {kind === 'slow_query' && g.avg_duration_ms != null && (
                          <>avg {fmtMs(g.avg_duration_ms)} · p95 {fmtMs(g.p95_duration_ms)} · max {fmtMs(g.max_duration_ms)} · </>
                        )}
                        {g.unique_actors} actor{Number(g.unique_actors) === 1 ? '' : 's'} ·{' '}
                        last {fmtTime(g.last_seen)} {isOpen ? '▲' : '▼'}
                      </span>
                    </button>

                    {isOpen && (
                      <div style={S.groupDetail}>
                        <div style={{ fontSize: '0.78rem', color: BRAND.textMuted, padding: '10px 0' }}>
                          First seen: {fmtTime(g.first_seen)} · Last seen: {fmtTime(g.last_seen)}
                          {g.codes && g.codes.length > 0 && <> · Codes: {g.codes.join(', ')}</>}
                          {g.app_versions && g.app_versions.length > 0 && <> · App versions: {g.app_versions.join(', ')}</>}
                        </div>
                        {occurrences.length === 0 ? (
                          <div style={{ color: BRAND.textLight, fontSize: '0.82rem', paddingBottom: '10px' }}>
                            Per-occurrence rows for this group fell outside the row cap.
                          </div>
                        ) : (
                          <table style={S.table}>
                            <thead>
                              <tr>{cfg.columns.map((c) => <th key={c.key} style={S.th}>{c.label}</th>)}</tr>
                            </thead>
                            <tbody>
                              {occurrences.map((r) => (
                                <tr key={r.id}>
                                  {cfg.columns.map((c) => (
                                    <td key={c.key} style={S.td}>{renderCell(r, c)}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>

        <div style={S.modalFooter}>
          Source: <code>telemetry_events</code> where <code>event_name = '{cfg.eventName}'</code>.
          {' '}{cfg.sourceFootnote}
        </div>
      </div>
    </div>
  );
}
